<template>
  <div>
    <div :class="$style.formGroup">
      <label for="shopSectionName" :class="$style.label">Section name *</label>
      <InputText
        id="shopSectionName"
        ref="shopSectionNameRef"
        v-model="shopSectionName"
        :class="{ [$style.pInvalid]: shopSectionName && (!isValid || isDuplicate) }"
        placeholder="Section name"
        autofocus
        @input="validate"
        @keydown.enter="save"
      />
      <small :class="[$style.info, shopSectionName && !isValid ? $style.pError : $style.pValid]">
        Use only alphanumeric characters,
      </small>
      <small v-if="isDuplicate" :class="[$style.info, $style.pError]">Section name already exists</small>
    </div>
    <div :class="$style.actions">
      <Button label="Cancel" class="p-button-primary" outlined @click="cancel" />
      <Button label="Save" class="p-button-primary" :disabled="!isValid || isDuplicate" @click="save" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import isAlphaNumeric from "@/utils/isAlphaNumeric";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import { ref } from "vue";
import { useRoute } from "vue-router";
import useShopsManager from "../hooks/useShopsManager";

const shopSectionNameRef = ref<HTMLInputElement | null>(null);

const route = useRoute();
const appId = route.params.gameId as string;
const versionId = route.params.versionId as string;
const props = defineProps({
  name: {
    type: String,
    required: false,
    default: "",
  },
  shopId: {
    type: String,
    required: true,
  },
});

const shopSectionName = ref<string>(props.name);
const isValid = ref(true);
const isDuplicate = ref(false);

const emit = defineEmits(["save-shop", "close"]);

const { isDuplicateShopSectionName } = useShopsManager(appId, versionId);

const validate = () => {
  isValid.value = isAlphaNumeric({ str: shopSectionName.value });
  isDuplicate.value =
    props.name !== shopSectionName.value && isDuplicateShopSectionName(shopSectionName.value, props.shopId);
};

const cancel = () => {
  shopSectionName.value = "";
  isValid.value = false;
  isDuplicate.value = false;
  emit("close");
};

const save = () => {
  validate();
  if (isValid.value && !isDuplicate.value) {
    emit("save-shop", shopSectionName.value);
    shopSectionName.value = "";
    isValid.value = false;
    isDuplicate.value = false;
  }
};
</script>

<style module>
.formGroup {
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
}

.label {
  margin-bottom: 0.5rem;
}

.pInvalid {
  border-color: var(--vd-color-error);
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.pError {
  color: var(--vd-color-error);
}

.pValid {
  color: var(--vd-color-text-light-2);
}

.info {
  margin-top: 0.5rem;
}
</style>
